import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Microphone, Stop, CircleNotch } from "@phosphor-icons/react";
import { transcribe } from "@/lib/api";

const LABEL = {
  idle: "Tap and tell me what you want to build",
  recording: "Listening… tap to finish",
  transcribing: "Turning your words into text…",
};

function useRecorder(onText) {
  const [state, setState] = useState("idle");
  const [level, setLevel] = useState(0);
  const [secs, setSecs] = useState(0);
  const refs = useRef({});

  const cleanup = () => {
    const r = refs.current;
    cancelAnimationFrame(r.raf);
    clearInterval(r.timer);
    r.stream?.getTracks().forEach((t) => t.stop());
    r.ctx?.close().catch(() => {});
    setLevel(0);
  };
  useEffect(() => cleanup, []); // eslint-disable-line

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = (refs.current = { stream, chunks: [] });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(an);
      r.ctx = ctx;
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        an.getByteFrequencyData(buf);
        setLevel(Math.min(1, buf.reduce((a, b) => a + b, 0) / buf.length / 90));
        r.raf = requestAnimationFrame(tick);
      };
      tick();
      setSecs(0);
      r.timer = setInterval(() => setSecs((s) => s + 1), 1000);
      const mr = new MediaRecorder(stream);
      r.mr = mr;
      mr.ondataavailable = (e) => e.data.size > 0 && r.chunks.push(e.data);
      mr.onstop = async () => {
        cleanup();
        const blob = new Blob(r.chunks, { type: "audio/webm" });
        if (!blob.size) return setState("idle");
        setState("transcribing");
        try {
          const text = await transcribe(blob);
          text ? onText(text) : toast.error("I couldn't hear anything. Try again a little closer to the mic.");
        } catch {
          toast.error("Transcription failed. Please try again.");
        } finally {
          setState("idle");
        }
      };
      mr.start();
      setState("recording");
    } catch {
      toast.error("Microphone access was blocked. Allow it in your browser, or type instead.");
      setState("idle");
    }
  };
  const stop = () => refs.current.mr?.state === "recording" && refs.current.mr.stop();
  return { state, level, secs, start, stop };
}

export function MicHero({ onTranscript, disabled, onStateChange }) {
  const { state, level, secs, start, stop } = useRecorder(onTranscript);
  useEffect(() => { onStateChange?.(state); }, [state]); // eslint-disable-line
  const rec = state === "recording";
  const busy = state === "transcribing";
  const click = () => (disabled || busy ? null : rec ? stop() : start());

  return (
    <div className="flex flex-col items-center" data-testid="mic-hero">
      <div className="relative flex h-56 w-56 items-center justify-center">
        <AnimatePresence>
          {(rec || busy) && (
            <motion.div
              key="glow"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 0.75, scale: 1 + level * 0.35 }}
              exit={{ opacity: 0, scale: 0.7 }}
              transition={{ type: "spring", stiffness: 120, damping: 14 }}
              className="stp-siri absolute h-44 w-44 rounded-full blur-2xl"
            />
          )}
        </AnimatePresence>
        {!rec && !busy && <div className="absolute h-40 w-40 rounded-full bg-brand/10 blur-xl" />}
        <motion.button
          type="button"
          data-testid="mic-hero-button"
          aria-label={rec ? "Stop recording" : "Start speaking"}
          onClick={click}
          disabled={disabled}
          whileHover={{ scale: disabled ? 1 : 1.04 }}
          whileTap={{ scale: 0.96 }}
          className={`relative z-10 flex h-36 w-36 items-center justify-center rounded-full shadow-[0_20px_50px_-12px_rgba(0,0,0,0.35)] transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-40 ${
            rec ? "bg-white text-[#ff2d55]" : "bg-ink text-white hover:bg-black"
          }`}
        >
          {busy ? (
            <CircleNotch size={52} weight="bold" className="animate-spin text-brand" />
          ) : rec ? (
            <Stop size={50} weight="fill" />
          ) : (
            <Microphone size={56} weight="fill" />
          )}
        </motion.button>
      </div>
      <p data-testid="mic-hero-status" className="mt-2 text-[15px] font-medium text-ink">
        {LABEL[state]}
      </p>
      <p className="mt-1 h-5 font-mono text-xs text-mist">
        {rec ? `0:${String(secs).padStart(2, "0")}` : busy ? "" : "Speak naturally — any language works"}
      </p>
    </div>
  );
}
